'use client';

import { BellIcon } from '@curology/ui-components-web/react/Icon/icons/BellIcon';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React from 'react';

import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { CHECKOUT_MODES } from '@/components/flow/steps/checkout/types';
import ProductSelectionAndCheckoutLineItems from '@/components/productSelectionAndCheckoutLineItems';
import { Highlight } from '@/components/squeeze';
import { DATA_KEYS } from '@/consts/dataKeys';
import { centsToDollarsAndCents } from '@/helpers/productSelectionAndCheckout/helpers';

import { useCheckoutMode } from '../../hooks/useCheckoutMode';
import { usePromotionClaim } from '../../hooks/usePromotionClaim';
import { useSelectedItemIds } from '../../hooks/useSelectedItemIds';
import { useTotals } from '../../hooks/useTotals';
import { useTrialSizeItemPrices } from '../../hooks/useTrialSizeItemPrices';
import { PromotionCodeInputAndDisplay } from '../promotionCodeInputAndDisplay';

export const PriceSummary = ({
  page,
}: {
  page: 'productSelection' | 'checkout';
}) => {
  const { treatmentCategory } = useFlowContext();
  const checkoutMode = useCheckoutMode();
  const {
    promotion,
    productSelection: { flags, fullSizeItemPrices },
  } = useDataProvider(DATA_KEYS.promotion, DATA_KEYS.productSelection);
  const { promotionClaim } = usePromotionClaim();
  const { selectedItemIds } = useSelectedItemIds();
  const numItems = selectedItemIds.length;
  const smallSizeItemPrices = useTrialSizeItemPrices();

  const {
    trialTotalNoTax,
    subscriptionTotalNoTax,
    smallAgencySubscriptionTotal,
  } = useTotals();

  const itemPrices =
    checkoutMode === CHECKOUT_MODES.Trial
      ? smallSizeItemPrices
      : fullSizeItemPrices;

  const disclaimer = flags.agencySmallBottlesInSignUp
    ? `After your trial ends, we’ll automatically send you a refill every 30 days for ${centsToDollarsAndCents(
        smallAgencySubscriptionTotal,
      )} (plus sales tax and discounts where applicable). Cancel anytime.`
    : `After your trial ends, we’ll automatically send you full-size products every 60 days for ${centsToDollarsAndCents(
        subscriptionTotalNoTax,
      )} (subject to change). That breaks down to just 
            ${centsToDollarsAndCents(
              subscriptionTotalNoTax / 2,
            )}/month. Cancel anytime.`;

  return (
    <div className="cur-bg-surface-neutral cur-p-4" id="price-summary">
      {flags.curologySkinPreShipmentNotification &&
        treatmentCategory === 'skin' && (
          <Highlight size="sm" className="cur-mb-2">
            <div className="cur-flex">
              <BellIcon className="cur-text-primary" />
              <Typography
                size="meta"
                weight="bold"
                className="cur-text-primary cur-pl-4"
              >
                We’ll notify you before your trial ends
              </Typography>
            </div>
          </Highlight>
        )}
      <ProductSelectionAndCheckoutLineItems
        itemPrices={itemPrices}
        subscriptionItemPrices={fullSizeItemPrices}
        page={page}
      />
      {/* As we prepare to experiment on Hair Rx pricing, we are hiding the Promo Code input
      for hair to ultimately make its visibility configurable via LaunchDarkly */}
      {flags.showPromoSection && treatmentCategory !== 'hair' && (
        <>
          <hr className="cur-bg-border-disabled cur-border-0 cur-h-[1px] cur-mt-2" />
          <PromotionCodeInputAndDisplay />
        </>
      )}
      <div className="cur-bg-surface-highlight cur-mt-4 cur-p-4">
        <div className="cur-flex cur-justify-between">
          <Typography>Total</Typography>
          <Typography weight="bold">
            {centsToDollarsAndCents(trialTotalNoTax)}
          </Typography>
        </div>
        {promotionClaim && promotion?.percentageOffActual && (
          <div className="cur-flex cur-justify-between">
            <Typography size="meta">
              ({numItems} Item{numItems !== 1 ? 's' : ''})
            </Typography>
            <Typography className="cur-text-brand" size="meta">
              {promotion.percentageOffActual}% discount applied
            </Typography>
          </div>
        )}
        {flags.displayHSACopy && (
          <Typography className="cur-pt-2" size="meta">
            Your purchase may be eligible for HSA/FSA reimbursement.
          </Typography>
        )}
      </div>
      <div className="cur-pt-4 cur-text-curology-secondary-400">
        <Typography size="meta">{disclaimer}</Typography>
      </div>
    </div>
  );
};
